import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Package,
  ArrowLeft,
  Menu,
  Wifi,
  WifiOff,
  User,
  LogOut,
  Home,
  ScanLine,
  ClipboardList,
  Box,
  Truck,
  MapPin
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import { useMobile } from '../context/MobileContext';
import { logout, getCurrentUser } from '@/shared/services/api';

const MobileLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline, deviceInfo, pendingScans } = useMobile();
  const user = getCurrentUser();

  const menuItems = [
    { name: 'Dashboard', href: '/mobile', icon: Home },
    { name: 'Receiving', href: '/mobile/receiving', icon: Package },
    { name: 'Put-away', href: '/mobile/putaway', icon: MapPin },
    { name: 'Picking', href: '/mobile/picking', icon: ClipboardList },
    { name: 'Packing', href: '/mobile/packing', icon: Box },
    { name: 'Load-out', href: '/mobile/loadout', icon: Truck },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/mobile') return 'EzERP Mobile';
    if (path.includes('/receiving')) return 'Receiving';
    if (path.includes('/putaway')) return 'Put-away';
    if (path.includes('/picking')) return 'Picking';
    if (path.includes('/packing')) return 'Packing';
    if (path.includes('/loadout')) return 'Load-out';
    return 'EzERP Mobile';
  };

  const canGoBack = location.pathname !== '/mobile';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Mobile Header - Uber style: black header */}
      <header className="sticky top-0 z-50 bg-black text-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {canGoBack ? (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/mobile')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              <Sheet>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-white hover:bg-white/10"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 bg-white">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2 text-black">
                      <Package className="h-5 w-5" />
                      EzERP Mobile
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="mt-6 space-y-2">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Button
                          key={item.href}
                          variant={isActive ? 'default' : 'ghost'}
                          className={`w-full justify-start gap-3 ${isActive ? 'bg-black text-white hover:bg-black/90' : 'text-black hover:bg-gray-100'}`}
                          onClick={() => navigate(item.href)}
                        >
                          <Icon className="h-5 w-5" />
                          {item.name}
                        </Button>
                      );
                    })}
                  </nav>
                  <div className="absolute bottom-6 left-4 right-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{user?.full_name || user?.username || user?.email || 'Operator'}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full gap-2 border-gray-300 text-black hover:bg-gray-100"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            )}
            <h1 className="text-lg font-semibold">{getPageTitle()}</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {pendingScans.length > 0 && (
              <Badge className="bg-green-500 text-white border-0">
                {pendingScans.length} pending
              </Badge>
            )}
            {isOnline ? (
              <Wifi className="h-5 w-5 text-green-400" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-400" />
            )}
          </div>
        </div>
      </header>

      {/* Offline Banner - Uber green accent */}
      {!isOnline && (
        <div className="bg-green-500 text-white text-center py-2 text-sm font-medium">
          You're offline. Scans will sync when connected.
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>

      {/* Bottom Navigation - Uber style: white with black icons */}
      <nav className="sticky bottom-0 bg-white border-t border-gray-200 safe-area-pb">
        <div className="flex justify-around py-2">
          {menuItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href || 
              (item.href !== '/mobile' && location.pathname.startsWith(item.href));
            return (
              <Button
                key={item.href}
                variant="ghost"
                size="sm"
                className={`flex-col h-auto py-2 px-3 gap-1 ${isActive ? 'text-black' : 'text-gray-400'}`}
                onClick={() => navigate(item.href)}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-green-500' : ''}`} />
                <span className="text-xs">{item.name.split('-')[0]}</span>
              </Button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
